<template>
    <v-container v-resize="onResize">
        <v-layout v-if="!this.loading" row wrap align-center>
            <v-flex xs12 md12 lg4>
                <h1>Referendums and Plebiscites</h1>
            </v-flex>
            <v-flex class="pt-xs-2" xs6 sm3 offset-md4 offset-lg0 md2 lg2>
                <count-up :endVal="referendumsCount" text="Referendums since Federation" />
            </v-flex>
            <v-flex xs6 sm3 md2 lg2>
                <count-up :endVal="referendumsPassedCount" text="Referendums carried" />
            </v-flex>
            <v-flex xs6 sm3 md2 lg2>
                <count-up :endVal="plebiscitesCount" text="Plebiscites since Federation" />
            </v-flex>
            <v-flex xs6 sm3 md2 lg2>
                <count-up :endVal="plebiscitesPassedCount" text="Plebiscites carried" />
            </v-flex>
        </v-layout>
        <v-layout row wrap align-center class="hidden-print-only">
            <v-flex xs12 md9 mt-4>
                <v-layout row wrap justify-space-between>
                    <v-flex grow class="handbook-leftDivider_hideSmall">
                        <v-btn-toggle v-model="referendumOptionSelected" class="transparent handbook_toggleButton" :mandatory="true">
                            <v-tooltip v-for="option in referendumOptions" :key="option.value" top disabled>
                                <template v-slot:activator="{ on }">
                                    <v-btn v-on="on" class="handbook_button" :value="option.value">
                                        <span class="long-button-text chamberButton">{{ option }}</span>
                                    </v-btn>
                                </template>
                            </v-tooltip>
                            <span></span>
                        </v-btn-toggle>
                    </v-flex>
                </v-layout>
            </v-flex>
            <v-flex xs12 md3 mt-4 mt-xs-2>
                <div>
                    <v-text-field :class="{mobileTextField: isMobile}" v-model="search" append-icon="search" label="Search" single-line hide-details></v-text-field>
                    <v-menu v-if="!this.loading && this.isMobile" d-inline offset-y :nudge-left="140" :close-on-content-click="true">
                        <v-btn outline small slot="activator">
                            Sort <v-icon>sort</v-icon>
                        </v-btn>
                        <v-list>
                             <v-subheader
                                >
                                Sort by:
                                </v-subheader>
                            <v-list-tile v-for="(item, index) in headersCalculated" :key="item.value" @click="changeSort(item.value)">
                                <v-list-tile-title>
                                    {{ item.text }}
                                    <v-icon v-if="PAGINATION.sortBy === item.value">{{PAGINATION.descending ? 'arrow_downward':'arrow_upward'}}</v-icon>
                                </v-list-tile-title>
                            </v-list-tile>
                        </v-list>
                    </v-menu>
                </div>
            </v-flex>
        </v-layout>

        <br/>

        <p>Data from the Australian Electoral Commission is added to the online handbook following return of the election writ(s).</p>

        <br/>

        <loader v-if="loading" />       
        <div class="referendums-table"> 
            <v-data-table 
                v-if="!loading"
                :headers="headersCalculated"
                :footer-props="FOOTERS"
                :items="itemsFiltered"
                :search="search"
                :rows-per-page-items="ROWS_LISTITEMS"
                :pagination.sync="PAGINATION"
                :hide-headers="isMobile || isPrint"
                :hide-actions="isPrint"
                :disable-initial-sort="true"
                :class="{ mobileDataTable: isMobile || isPrint, 'elevation-1': true }">
                <template v-slot:items="props">
                    <router-link v-if="isMobile || isPrint" class="tr" :to="{ name: 'Referendum', params: { id: props.item.Id }}">
                        <td>
                            <div class="flex-content">
                                <div class="flex-item"><h3 class="pb-xs-0">{{ props.item.Title }}</h3></div>
                                <div class="flex-item" data-label="Date">{{ props.item.Date | stringDateFormat }}</div>
                                <div class="flex-item" data-label="Question"><div v-html="props.item.Question"></div></div>
                                <div class="flex-item flex-item-col flex-item-col-40">
                                    <div class="flex-item" data-label="Prime Minister">
                                        <Portrait v-bind:PHID="props.item.PrimeMinister.Phid" :fixedWidth="50"></Portrait>
                                        <div>
                                            <span>{{ props.item.PrimeMinister.DisplayName }}</span> <br />
                                            <span>{{ props.item.PrimeMinister.Party }}</span>
                                        </div>
                                    </div>
                                </div>
                                <div class="flex-item flex-item-col flex-item-col-60">
                                    <div class="flex-item" data-label="Leader of the Opposition">
                                        <Portrait v-bind:PHID="props.item.LeaderOfOpposition.Phid" :fixedWidth="50"></Portrait>
                                        <div>
                                            <span>{{ props.item.LeaderOfOpposition.DisplayName }}</span> <br />
                                            <span>{{ props.item.LeaderOfOpposition.Party }}</span>
                                        </div>
                                    </div>
                                </div>
                                <div class="flex-item">
                                    <div class="flex-item flex-item-col flex-item-col-40" data-label="States voting yes">
                                        <span v-if="props.item.VoteMajority != 0">{{ props.item.StateMajority }}</span>
                                        <span v-if="props.item.VoteMajority == 0">N/A</span>
                                    </div>
                                    <div class="flex-item flex-item-col flex-item-col-60" data-label="Total yes votes">
                                        <span v-if="props.item.VoteMajority != 0">{{ props.item.VoteMajority }}%</span>
                                        <span v-if="props.item.VoteMajority == 0">N/A</span>
                                    </div>
                                    <div class="flex-item" data-label="Carried">
                                        <span style="vertical-align: top; display: inline-block" class="pt-1">{{ props.item.Carried | isBoolean }}</span>
                                        <v-icon aria-hidden="true" class="ml-2" :color="props.item.Carried ? 'green darken-2' : 'red darken-2'">
                                            {{ props.item.Carried ? 'check_circle' : 'cancel' }}
                                        </v-icon> 
                                    </div>
                                </div>
                            </div>
                        </td>
                    </router-link>
                    <router-link v-else class="tr" :to="{ name: 'Referendum', params: { id: props.item.Id }}">
                        <td class="text-xs-left" v-if="referendumOptionSelected == 0">{{ props.item.Number }}</td>
                        <td class="text-xs-left">{{ props.item.Date | stringDateFormat }}</td>
                        <td class="text-xs-left font-weight-bold">{{ props.item.Title }}</td>
                        <td class="text-xs-left">
                            <br/>
                            <div v-html="props.item.Question"></div>
                        </td>
                        <td class="text-xs-left">
                            <Portrait v-bind:PHID="props.item.PrimeMinister.Phid" :fixedWidth="50"></Portrait>
                            <div>
                                <span>{{ props.item.PrimeMinister.DisplayName }}</span> <br />
                                <p style="line-height: 1rem;">{{ props.item.PrimeMinister.Party }}</p>
                            </div>
                        </td>
                        <td class="text-xs-left">
                            <Portrait v-bind:PHID="props.item.LeaderOfOpposition.Phid" :fixedWidth="50"></Portrait>
                            <div>
                                <span>{{ props.item.LeaderOfOpposition.DisplayName }}</span> <br />
                                <p style="line-height: 1rem;">{{ props.item.LeaderOfOpposition.Party }}</p>
                            </div>
                        </td>
                        <td class="text-xs-left">
                            <span v-if="props.item.VoteMajority != 0">{{ props.item.StateMajority }}</span>
                            <span v-if="props.item.VoteMajority == 0">N/A</span>
                        </td>
                        <td class="text-xs-left">
                            <span v-if="props.item.VoteMajority != 0">{{ props.item.VoteMajority }}%</span>
                            <span v-if="props.item.VoteMajority == 0">N/A</span>
                        </td>
                        <td class="text-xs-left">
                            <span style="vertical-align: top; display: inline-block" class="pt-1">{{ props.item.Carried | isBoolean }}</span>
                            <v-icon aria-hidden="true" class="ml-2" :color="props.item.Carried ? 'green darken-2' : 'red darken-2'">
                                {{ props.item.Carried ? 'check_circle' : 'cancel' }}
                            </v-icon> 
                        </td>
                    </router-link>
                </template>
            </v-data-table>     
        </div>   
        <br/>
        <v-layout row wrap class="hidden-print-only">
            <v-flex xs12>
                <v-btn class="csvButton" @click="exportCsv" v-on:keyup.enter="exportCsv" color="primary">
                    <v-icon aria-hidden="true" :class="csvDownloadClass">{{csvDownloadIcon}}</v-icon>
                    <span>&nbsp;Download detailed <span v-if="referendumOptionSelected == 0">referendums</span><span v-else-if="referendumOptionSelected == 1">plebiscites</span><span v-else>national surveys</span> CSV</span>
                </v-btn>
                <v-btn class="csvButton" @click="exportSummaryCsv" v-on:keyup.enter="exportSummaryCsv" color="primary">
                    <v-icon aria-hidden="true" :class="csvSummaryDownloadClass">{{csvSummaryDownloadIcon}}</v-icon>
                    <span>&nbsp;Download <span v-if="referendumOptionSelected == 0">referendums</span><span v-else-if="referendumOptionSelected == 1">plebiscites</span><span v-else>national surveys</span> summary CSV</span>
                </v-btn>
            </v-flex>
        </v-layout>
    </v-container>
</template>

<script>    
    import print from '../../mixins/print';
    import Loader from '@/components/Loader.vue';
    import axios from 'axios';
    import CountUp from "../StatisticalInformation/CountUp.vue";
    window.Handbook = window.Handbook || {}
    const apiUri = window.Handbook.ApiUri || "https://localhandbookapi.aph.gov.au/";
    axios.defaults.baseURL = apiUri;
    import Portrait from '@/components/Portrait.vue'

    export default {
        created() {
            this.initialiseItems();
            this.onResize();
        },
        mixins: [print],
        components: {
            Loader,
            CountUp,
            Portrait
        },
        data() {
            return {
                isMobile: false,
                search: '',
                items: [],
                referendumsCount: 0,
                referendumsPassedCount: 0,
                plebiscitesCount: 0,
                plebiscitesPassedCount: 0,
                loading: true,
                csvDownloading: false,
                csvSummaryDownloading: false,
                referendumOptionSelected: 0,
                referendumOptions: ['Referendums', 'Plebiscites', 'National Surveys'],
                ROWS_LISTITEMS: [5, 10, 50, {
                    "text": "$vuetify.dataIterator.rowsPerPageAll",
                    "value": -1
                }],
                PAGINATION: {
                    rowsPerPage: this.$store.state.ROWS_PER_PAGE,
                    sortBy: 'Number',
                    descending: true
                },
                referendumHeaders: [{
                    text: 'Number',
                    value: 'Number',
                    align: 'left',
                    sortable: true,
                    width: "70px"
                },
                {
                    text: 'Date',
                    value: 'Date',
                    align: 'left',
                    sortable: true,
                },
                {
                    text: 'Title',
                    value: 'Title',
                    align: 'left',
                    sortable: true
                },
                {
                    text: 'Question',
                    value: 'Question',
                    align: 'left',
                    sortable: true,
                },
                {
                    text: 'Prime Minister',
                    value: 'PrimeMinister.DisplayName',
                    align: 'left',
                    sortable: true,
                    width: "130px"
                },
                {
                    text: 'Leader of the Opposition',
                    value: 'LeaderOfOpposition.DisplayName',
                    align: 'left',
                    sortable: true,
                    width: "130px"
                },
                {
                    text: 'States voting yes',
                    value: 'StateMajority',
                    align: 'left',
                    sortable: true,
                    width: "100px"
                },
                {
                    text: 'Total yes votes',
                    value: 'VoteMajority',
                    align: 'left',
                    sortable: true,
                    width: "110px"
                },
                {
                    text: 'Carried',
                    value: 'Carried',
                    align: 'left',
                    sortable: true,
                    width: "90px"
                }],
                plebisciteHeaders: [{
                    text: 'Date',
                    value: 'Date',
                    align: 'left',
                    sortable: true,
                },
                {
                    text: 'Title',
                    value: 'Title',
                    align: 'left',
                    sortable: true
                },
                {
                    text: 'Question',
                    value: 'Question',
                    align: 'left',
                    sortable: true,
                },
                {
                    text: 'Prime Minister',
                    value: 'PrimeMinister.DisplayName',
                    align: 'left',
                    sortable: true,
                    width: "130px"
                },
                {
                    text: 'Leader of the Opposition',
                    value: 'LeaderOfOpposition.DisplayName',
                    align: 'left',
                    sortable: true,
                    width: "130px"
                },
                {
                    text: 'States voting yes',
                    value: 'StateMajority',
                    align: 'left',
                    sortable: true,
                    width: "100px"
                },
                {
                    text: 'Total yes votes',
                    value: 'VoteMajority',
                    align: 'left',
                    sortable: true,
                    width: "110px"
                },
                {
                    text: 'Carried',
                    value: 'Carried',
                    align: 'left',
                    sortable: true,
                    width: "90px"
                }],
                FOOTERS: {
                    showFirstLastPage: true,
                    firstIcon: 'mdi-arrow-collapse-left',
                    lastIcon: 'mdi-arrow-collapse-right',
                    prevIcon: 'mdi-minus',
                    nextIcon: 'mdi-plus',
                },
            }
        },
        computed: {
            headersCalculated: function() {
                return this.referendumOptionSelected == 0 ? this.referendumHeaders : this.plebisciteHeaders;
            },
            itemsFiltered: function() {
                switch(this.referendumOptionSelected) {
                    case 0: 
                    return this
                        .items
                        .filter(r => r.ReferendumType === 'Referendum' );
                    case 1: 
                    return this
                        .items
                        .filter(r => r.ReferendumType === 'Plebiscite' );
                    case 2: 
                    return this
                        .items
                        .filter(r => r.ReferendumType === 'National Survey' );
                    default:
                        return this.items;
                }
            },
            csvDownloadIcon(){
                return this.csvDownloading ? 'cached' : 'text_snippet';
            },
            csvDownloadClass(){
                let csvClass = 'mr-1';
                if (this.csvDownloading){
                    csvClass = csvClass + ' loading'
                }
                return csvClass;
            },
            csvSummaryDownloadIcon(){
                return this.csvSummaryDownloading ? 'cached' : 'text_snippet';
            },
            csvSummaryDownloadClass(){
                let csvClass = 'mr-1';
                if (this.csvSummaryDownloading){
                    csvClass = csvClass + ' loading'
                }
                return csvClass;
            },
        },
        mounted() {
            this.onResize();
            //Set page to 10 on mobile, only on mount
            if (this.isMobile){
                this.PAGINATION.rowsPerPage = 10;
            }
        },
        methods: {
            onResize() {
                if (window.innerWidth < 1060) {
                    this.isMobile = true;
                }
                else {
                    this.isMobile = false;
                }
            },
            changeSort(column) {
                if (this.PAGINATION.sortBy === column) {
                    this.PAGINATION.descending = !this.PAGINATION.descending
                } 
                else {
                    this.PAGINATION.sortBy = column
                    this.PAGINATION.descending = false
                }
            },
            initialiseItems() {
                this.loading = true;
                const url = 'Referendums/Referendums';
                axios.get(`api/${url}`)
                    .then(response => {
                        this.items = response.data;
                        this.loading = false;
                        this.plebiscitesCount = this.items.filter(r => r.ReferendumType === 'Plebiscite').length;
                        this.referendumsCount = this.items.filter(r => r.ReferendumType === 'Referendum').length;
                        this.referendumsPassedCount = this.items.filter(r => r.ReferendumType === 'Referendum' && r.Carried).length;
                        this.plebiscitesPassedCount = this.items.filter(r => r.ReferendumType === 'Plebiscite' && r.Carried).length;
                    });
            },
            exportCsv() {
                this.$gtag.event('referendums download csv', {
                    'event_category': 'downloads',
                    'event_label': `referendums csv download`,
                    'value': 1
                });

                let records = [];
                this.csvDownloading = true;
                let headers = this.referendumOptionSelected == 0 ? ['Number', 'Date', 'Title', 'Question', 'State', 'Division', 'Enrolled', 'Votes', 'For Votes',
                 'For Percentage', 'Against Votes', 'Against Percentage', 'Informal Votes'] : ['Date', 'Title', 'Question', 'State', 'Division', 'Enrolled', 'Votes', 'For Votes',
                 'For Percentage', 'Against Votes', 'Against Percentage', 'Informal Votes'];

                let filename = '';
                switch(this.referendumOptionSelected) {
                    case 0: 
                    filename = 'Detailed Referendums';
                    break;
                    case 1: 
                    filename = 'Detailed Plebiscites';
                    break;
                    case 2: 
                    filename = 'Detailed National Surveys';
                    break;
                }

                const url = 'Referendums/ReferendumsAll';
                axios.get(`api/${url}`)
                    .then(response => {
                        const referendums = response.data;
                        // for each referendum
                        referendums
                            .filter(r => {
                                switch(this.referendumOptionSelected) {
                                    case 0: return r.ReferendumType === 'Referendum';
                                    case 1: return r.ReferendumType === 'Plebiscite';
                                    case 2: return r.ReferendumType === 'National Survey';
                                    default: return true;
                                }
                            })
                            .forEach(r => {
                            // for each division
                            r.Results.forEach(d => {
                                // add row
                                if (this.referendumOptionSelected == 0){
                                    records.push({
                                        'Number': r.Number,
                                        'Date': r.Date ? new Date(r.Date).toLocaleDateString() : null,
                                        'Title': r.Title,
                                        'Question': r.Question.replace("\r\n", "").replace(/<\/?[^>]+(>|$)/g, "").replace(/[\r\n]+/g," "),
                                        'Vote Type': r.ReferendumType,
                                        'State': d.StateAbbrev,
                                        'Division': d.Division,
                                        'Enrolled': d.Enrolled,
                                        'Votes': d.Votes,
                                        'For Votes': d.ForVotes || 'N/A',
                                        'For Percentage': d.ForPercentage || 'N/A',
                                        'Against Votes': d.AgainstVotes || 'N/A',
                                        'Against Percentage': d.AgainstPercentage || 'N/A',
                                        'Informal Votes': d.InformalVotes
                                    });
                                }
                                else {
                                    records.push({
                                        'Date': r.Date ? new Date(r.Date).toLocaleDateString() : null,
                                        'Title': r.Title,
                                        'Question': r.Question.replace("\r\n", "").replace(/<\/?[^>]+(>|$)/g, "").replace(/[\r\n]+/g," "),
                                        'Vote Type': r.ReferendumType,
                                        'State': d.StateAbbrev,
                                        'Division': d.Division,
                                        'Enrolled': d.Enrolled,
                                        'Votes': d.Votes,
                                        'For Votes': d.ForVotes || 'N/A',
                                        'For Percentage': d.ForPercentage || 'N/A',
                                        'Against Votes': d.AgainstVotes || 'N/A',
                                        'Against Percentage': d.AgainstPercentage || 'N/A',
                                        'Informal Votes': d.InformalVotes
                                    });
                                }
                            });
                        });

                        this.$store.dispatch('GETCSV', { DATA: records, HEADERS: headers, FILENAME: filename })
                            .catch(this.showError)
                            .finally(() => this.csvDownloading = false);
                    });
            },
            exportSummaryCsv() {
                this.$gtag.event('referendums summary download csv', {
                    'event_category': 'downloads',
                    'event_label': `referendums summary csv download`,
                    'value': 1
                });
                
                this.csvSummaryDownloading = true;
                let headers = this.referendumOptionSelected == 0 ? ['Number', 'Date', 'Title', 'Question', 'Prime Minister', 'State voting yes', 'Total yes votes', 'Carried',
                 'First introduced to Parliament link', 'Bill link', 'Third reading in Senate vote link', 'Third reading in House vote link', 'Notes' ] 
                 : ['Date', 'Title', 'Question', 'Prime Minister', 'State voting yes', 'Total yes votes', 'Carried']

                let filename = '';
                switch(this.referendumOptionSelected) {
                    case 0: 
                    filename = 'Referendums Summary';
                    break;
                    case 1: 
                    filename = 'Detailed Plebiscites';
                    break;
                    case 2: 
                    filename = 'Detailed National Surveys';
                    break;
                }

                let includeBillLink = this.items.filter(r => r.BillLink != null).length > 0;
                let includeIntroducedLink = this.items.filter(r => r.IntroducedLink != null).length > 0;
                let includeSenateLink = this.items.filter(r => r.SenateVoteLink != null).length > 0;
                let includeHouseLink = this.items.filter(r => r.HouseVoteLink != null).length > 0;
                let includeNotes = this.items.filter(r => r.Notes != null).length > 0;

                let records = this.itemsFiltered
                .map(r => {
                    if (this.referendumOptionSelected == 0){
                        return {
                            'Number': r.Number,
                            'Date': r.Date ? new Date(r.Date).toLocaleDateString() : null,
                            'Title': r.Title,
                            'Question': r.Question.replace("\r\n", "").replace(/<\/?[^>]+(>|$)/g, "").replace(/[\r\n]+/g," "),
                            'Vote Type': r.ReferendumType,
                            'Prime Minister': r.PrimeMinister.DisplayName,
                            'Prime Minister Party': r.PrimeMinister.Party,
                            'Prime Minister Phid': r.PrimeMinister.Phid,
                            'Leader of the Opposition': r.LeaderOfOpposition.DisplayName,
                            'Leader of the Opposition Party': r.LeaderOfOpposition.Party,
                            'Leader of the Opposition Phid': r.LeaderOfOpposition.Phid,
                            'State voting yes': ` ${r.StateMajority}`,
                            'Total yes votes': r.VoteMajority ? (r.VoteMajority / 100).toFixed(2) : 'N/A',
                            'Carried': r.Carried,
                            'First introducted to Parliament link': r.IntroducedLink,
                            'Bill link': r.BillLink,
                            'Third reading vote in Senate link': r.SenateVoteLink,
                            'Third reading vote in House link': r.HouseVoteLink,
                            'Notes': r.Notes
                        }
                    }
                    else {
                        return {
                            'Date': r.Date ? new Date(r.Date).toLocaleDateString() : null,
                            'Title': r.Title,
                            'Question': r.Question.replace("\r\n", "").replace(/<\/?[^>]+(>|$)/g, "").replace(/[\r\n]+/g," "),
                            'Vote Type': r.ReferendumType,
                            'Prime Minister': r.PrimeMinister.DisplayName,
                            'Prime Minister Party': r.PrimeMinister.Party,
                            'Prime Minister Phid': r.PrimeMinister.Phid,
                            'Leader of the Opposition': r.LeaderOfOpposition.DisplayName,
                            'Leader of the Opposition Party': r.LeaderOfOpposition.Party,
                            'Leader of the Opposition Phid': r.LeaderOfOpposition.Phid,
                            'States voting yes': ` ${r.StateMajority}`,
                            'Total yes votes': r.VoteMajority ? (r.VoteMajority / 100).toFixed(2) : 'N/A',
                            'Carried': r.Carried
                        }
                    }
                });

                records.forEach(function(v){ if (!includeIntroducedLink) { delete v["First introducted to Parliament link"] } });
                records.forEach(function(v){ if (!includeBillLink) { delete v["Bill link"] } });
                records.forEach(function(v){ if (!includeSenateLink) { delete v["Third reading vote in Senate link"] } });
                records.forEach(function(v){ if (!includeHouseLink) { delete v["Third reading vote in House link"] } });
                records.forEach(function(v){ if (!includeNotes) { delete v["Notes"] } });

                this.$store.dispatch('GETCSV', { DATA: records, HEADERS: headers, FILENAME: filename})
                    .catch(this.showError)
                    .finally(() => this.csvSummaryDownloading = false);
            }
        },
    }
</script>
<style lang="stylus" scoped>
    .v-progress-circular {
        margin: 1rem
    }

    a {
        color: #fff;
        text-decoration: underline;
    }

    .v-datatable .tr {
        display:table-row;
    }

    .v-datatable a.tr {
        color: #0e1531de;
        text-decoration:none;
        border: 1px solid #dbdbdb;
        border-left: none;
        border-right: none;
    }

    .v-datatable tbody td {
        vertical-align: middle;
    }
    
    @media screen and (max-width: 1420px) {
        .referendums-table >>> table.v-table thead td:not(:nth-child(1)), 
        .referendums-table >>> table.v-table tbody td:not(:nth-child(1)), 
        .referendums-table >>> table.v-table thead th:not(:nth-child(1)), 
        .referendums-table >>> table.v-table tbody th:not(:nth-child(1)), 
        .referendums-table >>> table.v-table thead td:first-child, 
        .referendums-table >>> table.v-table tbody td:first-child, 
        .referendums-table >>> table.v-table thead th:first-child, 
        .referendums-table >>> table.v-table tbody th:first-child,
        .referendums-table >>> table.v-table thead th:first-child {
            padding: 0 14px !important;
        }

        .referendums-table >>> th {
            white-space: break-spaces;
        }
    }
</style>
